import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { createToonGradientMap, toonMat } from './materials.js';

const WALL_LIFE = 8;
const MAX_WALLS_PER_BOT = 3;
const BOT_WALL_HP = 80;
const REINFORCE_HP_THRESHOLD = 40;
const REINFORCE_CHANCE_PER_SEC = 0.12;
const REINFORCE_AMOUNT = 25;
const REINFORCE_PULSE_LIFE = 0.45;

type BotWall = {
  mesh: THREE.Mesh;
  body: CANNON.Body;
  life: number;
  botId: number;
  hp: number;
  maxHp: number;
  pulseLife: number;
};

/** Bot-placed cover walls — solid physics builds that expire or break under fire. */
export class FpsBotBuilds {
  private readonly walls: BotWall[] = [];
  private readonly perBotCount = new Map<number, number>();
  private readonly grad = createToonGradientMap();

  constructor(
    private readonly world: CANNON.World,
    private readonly floorMat: CANNON.Material,
    private readonly sceneRoot: THREE.Group,
  ) {}

  /** Place a wall between bot and threat; returns true if placed. */
  tryPlaceWall(botId: number, botX: number, botZ: number, threatX: number, threatZ: number): boolean {
    const count = this.perBotCount.get(botId) ?? 0;
    if (count >= MAX_WALLS_PER_BOT) return false;

    const dx = botX - threatX;
    const dz = botZ - threatZ;
    const dist = Math.hypot(dx, dz) || 1;
    const nx = dx / dist;
    const nz = dz / dist;
    const wx = botX + nx * 1.35;
    const wz = botZ + nz * 1.35;
    const yaw = Math.atan2(nx, nz);

    const mat = toonMat(0x5a7a9a, this.grad, { emissive: 0x1a2840, emissiveIntensity: 0.18 });
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(3, 3, 0.15), mat);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.position.set(wx, 1.5, wz);
    mesh.rotation.y = yaw;
    mesh.userData.isBotBuild = true;
    mesh.userData.buildOwner = 'bot';
    mesh.userData.buildHp = BOT_WALL_HP;
    mesh.userData.buildMaxHp = BOT_WALL_HP;
    mesh.userData.baseEmissive = mat.emissive.getHex();
    mesh.userData.baseEmissiveIntensity = mat.emissiveIntensity;
    this.attachBotOwnerOutline(mesh);
    this.sceneRoot.add(mesh);

    const body = new CANNON.Body({ mass: 0, material: this.floorMat });
    body.addShape(new CANNON.Box(new CANNON.Vec3(1.5, 1.5, 0.075)));
    body.position.set(wx, 1.5, wz);
    body.quaternion.setFromAxisAngle(new CANNON.Vec3(0, 1, 0), yaw);
    this.world.addBody(body);

    this.walls.push({
      mesh,
      body,
      life: WALL_LIFE,
      botId,
      hp: BOT_WALL_HP,
      maxHp: BOT_WALL_HP,
      pulseLife: 0,
    });
    this.perBotCount.set(botId, count + 1);
    return true;
  }

  /** Place a ramp behind/on a wall to climb or take cover; returns true if placed. */
  tryPlaceRamp(botId: number, botX: number, botZ: number, threatX: number, threatZ: number): boolean {
    const count = this.perBotCount.get(botId) ?? 0;
    if (count >= MAX_WALLS_PER_BOT) return false;

    const dx = botX - threatX;
    const dz = botZ - threatZ;
    const dist = Math.hypot(dx, dz) || 1;
    const nx = dx / dist;
    const nz = dz / dist;

    const rx = botX + nx * 0.2;
    const rz = botZ + nz * 0.2;
    const ry = 1.0;

    const yaw = Math.atan2(nx, nz);

    const mat = toonMat(0x5a7a9a, this.grad, { emissive: 0x1a2840, emissiveIntensity: 0.18 });
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(3, 0.15, 3), mat);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.position.set(rx, ry, rz);

    const qYaw = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
    const qTilt = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI / 4);
    const qFinal = qYaw.multiply(qTilt);
    mesh.quaternion.copy(qFinal);

    mesh.userData.isBotBuild = true;
    mesh.userData.buildOwner = 'bot';
    mesh.userData.buildHp = BOT_WALL_HP;
    mesh.userData.buildMaxHp = BOT_WALL_HP;
    mesh.userData.baseEmissive = mat.emissive.getHex();
    mesh.userData.baseEmissiveIntensity = mat.emissiveIntensity;
    this.attachBotOwnerOutline(mesh);
    this.sceneRoot.add(mesh);

    const body = new CANNON.Body({ mass: 0, material: this.floorMat });
    body.addShape(new CANNON.Box(new CANNON.Vec3(1.5, 0.075, 1.5)));
    body.position.set(rx, ry, rz);
    body.quaternion.set(qFinal.x, qFinal.y, qFinal.z, qFinal.w);
    this.world.addBody(body);

    this.walls.push({
      mesh,
      body,
      life: WALL_LIFE,
      botId,
      hp: BOT_WALL_HP,
      maxHp: BOT_WALL_HP,
      pulseLife: 0,
    });
    this.perBotCount.set(botId, count + 1);
    return true;
  }

  /** Apply weapon damage to a bot wall mesh (or child). Returns true if the wall was destroyed. */
  damageWall(object: THREE.Object3D, amount: number): boolean {
    const idx = this.findWallIndexFromObject(object);
    if (idx === -1 || amount <= 0) return false;
    const wall = this.walls[idx]!;
    wall.hp = Math.max(0, wall.hp - amount);
    wall.mesh.userData.buildHp = wall.hp;
    this.refreshWallVisual(wall);
    if (wall.hp <= 0) {
      this.removeWallAtIndex(idx);
      return true;
    }
    return false;
  }

  getWallHp(object: THREE.Object3D): { hp: number; maxHp: number } | null {
    const idx = this.findWallIndexFromObject(object);
    if (idx === -1) return null;
    const wall = this.walls[idx]!;
    return { hp: wall.hp, maxHp: wall.maxHp };
  }

  isBotBuildObject(object: THREE.Object3D): boolean {
    return this.findWallIndexFromObject(object) !== -1;
  }

  getWallBotId(object: THREE.Object3D): number {
    const idx = this.findWallIndexFromObject(object);
    if (idx === -1) return -1;
    return this.walls[idx]!.botId;
  }

  getMeshesForRaycast(): THREE.Object3D[] {
    return this.walls.map((w) => w.mesh);
  }

  update(dt: number): void {
    for (const w of this.walls) {
      w.life -= dt;

      if (w.hp > 0 && w.hp < REINFORCE_HP_THRESHOLD && Math.random() < REINFORCE_CHANCE_PER_SEC * dt) {
        const prev = w.hp;
        w.hp = Math.min(w.maxHp, w.hp + REINFORCE_AMOUNT);
        w.mesh.userData.buildHp = w.hp;
        if (w.hp > prev) w.pulseLife = REINFORCE_PULSE_LIFE;
      }

      if (w.pulseLife > 0) {
        w.pulseLife = Math.max(0, w.pulseLife - dt);
      }

      this.refreshWallVisual(w);
    }

    for (let i = this.walls.length - 1; i >= 0; i--) {
      if (this.walls[i]!.life <= 0) {
        this.removeWallAtIndex(i);
      }
    }
  }

  dispose(): void {
    for (let i = this.walls.length - 1; i >= 0; i--) {
      this.removeWallAtIndex(i);
    }
    this.perBotCount.clear();
    this.grad.dispose();
  }

  private findWallIndexFromObject(object: THREE.Object3D): number {
    let node: THREE.Object3D | null = object;
    while (node) {
      if (node instanceof THREE.Mesh && node.userData.isBotBuild === true) {
        return this.walls.findIndex((w) => w.mesh === node);
      }
      node = node.parent;
    }
    return -1;
  }

  private refreshWallVisual(wall: BotWall): void {
    const mat = wall.mesh.material as THREE.MeshToonMaterial;
    const baseE = (wall.mesh.userData.baseEmissive as number | undefined) ?? 0x1a2840;
    const baseI = (wall.mesh.userData.baseEmissiveIntensity as number | undefined) ?? 0.18;
    const damageT = 1 - wall.hp / wall.maxHp;
    const emissive = new THREE.Color(baseE);
    if (damageT > 0.001) {
      emissive.lerp(new THREE.Color(0xff7722), damageT * 0.58);
    }
    if (wall.pulseLife > 0) {
      const pulseT = wall.pulseLife / REINFORCE_PULSE_LIFE;
      emissive.lerp(new THREE.Color(0x44ff88), pulseT * 0.82);
    }
    mat.emissive.copy(emissive);
    mat.emissiveIntensity = baseI + damageT * 0.26 + (wall.pulseLife > 0 ? 0.28 : 0);
    const ownerOutline = wall.mesh.userData.ownerOutline as THREE.Mesh | undefined;
    if (ownerOutline?.material instanceof THREE.MeshBasicMaterial) {
      const hpRatio = wall.hp / wall.maxHp;
      const pulse = hpRatio < 0.34 ? 0.5 + 0.5 * Math.sin(performance.now() * 0.035) : 0;
      ownerOutline.material.color.setHex(hpRatio < 0.34 ? 0xff6b24 : 0xff5f3d);
      ownerOutline.material.opacity = hpRatio < 0.34 ? 0.18 + pulse * 0.08 : 0.1;
      ownerOutline.scale.setScalar(hpRatio < 0.34 ? 1.018 + pulse * 0.012 : 1.012);
    }
  }

  private attachBotOwnerOutline(mesh: THREE.Mesh): void {
    const ownerMat = new THREE.MeshBasicMaterial({
      color: 0xff5f3d,
      transparent: true,
      opacity: 0.1,
      wireframe: true,
      depthWrite: false,
    });
    const ownerOutline = new THREE.Mesh(mesh.geometry.clone(), ownerMat);
    ownerOutline.name = 'bot-build-owner-outline';
    ownerOutline.raycast = () => {};
    ownerOutline.renderOrder = 7;
    ownerOutline.scale.setScalar(1.012);
    mesh.add(ownerOutline);
    mesh.userData.ownerOutline = ownerOutline;
  }

  private removeWallAtIndex(idx: number): void {
    const wall = this.walls[idx];
    if (!wall) return;
    this.world.removeBody(wall.body);
    wall.mesh.removeFromParent();
    wall.mesh.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh) || obj === wall.mesh) return;
      obj.geometry.dispose();
      if (Array.isArray(obj.material)) obj.material.forEach((mat) => mat.dispose());
      else obj.material.dispose();
    });
    wall.mesh.geometry.dispose();
    (wall.mesh.material as THREE.Material).dispose();
    const prev = this.perBotCount.get(wall.botId) ?? 1;
    const next = prev - 1;
    if (next <= 0) this.perBotCount.delete(wall.botId);
    else this.perBotCount.set(wall.botId, next);
    this.walls.splice(idx, 1);
  }
}
